import React, { useMemo } from "react";
import {
  Input,
  Textarea,
  Select,
  SelectItem,
  Switch,
  Button,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { useWorkflowStore } from "@/stores/WorkflowStore";
import { usePlaygroundStore } from "@/stores/PlaygroundStore";
import { useAvailableModels } from "@/hooks/playground/useAvailableModels";

interface TeamPropertiesPanelProps {
  teamId: string;
}

export const TeamPropertiesPanel: React.FC<TeamPropertiesPanelProps> = ({ teamId }) => {
  const { t } = useTranslation();
  const { workflow, setWorkflow } = useWorkflowStore();
  const { setSelectedNode } = usePlaygroundStore();
  const availableModels = useAvailableModels();

  const team = workflow?.teams?.[teamId];

  // Compute available agents that can be added as members
  const availableAgents = useMemo(() => {
    if (!workflow || !team) return [];

    const currentMembers = new Set(team.members);

    return Object.keys(workflow.agents).filter((agentId) => !currentMembers.has(agentId));
  }, [workflow, team]);

  if (!team || !workflow) {
    return (
      <div className="p-4 text-center text-default-500">
        <Icon className="w-8 h-8 mx-auto mb-2" icon="solar:users-group-rounded-linear" />
        <p className="text-sm">{t("Team not found")}</p>
      </div>
    );
  }

  const updateTeamField = (key: string, value: any) => {
    const updatedWorkflow = {
      ...workflow,
      teams: {
        ...workflow.teams,
        [teamId]: {
          ...workflow.teams![teamId],
          [key]: value,
        },
      },
    };

    setWorkflow(updatedWorkflow);
  };

  const addMember = (memberId: string) => {
    if (!team.members.includes(memberId)) {
      updateTeamField("members", [...team.members, memberId]);
    }
  };

  const removeMember = (memberId: string) => {
    updateTeamField(
      "members",
      team.members.filter((m) => m !== memberId),
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-purple-500" icon="solar:users-group-rounded-linear" />
        <h3 className="text-sm font-semibold">{t("Team Configuration")}</h3>
      </div>

      {/* Strategy */}
      <Select
        label={t("Strategy")}
        selectedKeys={team.strategy ? [team.strategy] : ["selector"]}
        size="sm"
        onSelectionChange={(keys) => {
          const selected = Array.from(keys)[0] as string;

          if (selected) updateTeamField("strategy", selected);
        }}
      >
        <SelectItem key="selector">{t("Selector")}</SelectItem>
        <SelectItem key="mixture">{t("Mixture of Agents")}</SelectItem>
      </Select>

      {/* Goal */}
      <Textarea
        label={t("Goal")}
        maxRows={4}
        minRows={2}
        placeholder={t("Team objective...")}
        size="sm"
        value={team.goal || ""}
        onValueChange={(value) => updateTeamField("goal", value)}
      />

      {/* Max Cycles */}
      <Input
        label={t("Max Cycles")}
        size="sm"
        type="number"
        value={String(team.maxCycles || 10)}
        onValueChange={(value) => updateTeamField("maxCycles", parseInt(value) || 10)}
      />

      {/* Model (for orchestration) */}
      <Select
        label={t("Orchestration Model")}
        placeholder={t("Select model")}
        selectedKeys={team.model ? [team.model] : []}
        size="sm"
        onSelectionChange={(keys) => {
          const selected = Array.from(keys)[0] as string;

          if (selected) updateTeamField("model", selected);
        }}
      >
        {availableModels.map((model) => (
          <SelectItem key={model.id}>{model.name}</SelectItem>
        ))}
      </Select>

      {/* Use LLM Judgement */}
      <div className="flex items-center justify-between">
        <label className="text-xs text-default-600">{t("Use LLM Judgement")}</label>
        <Switch
          isSelected={team.useLlmJudgment !== false}
          size="sm"
          onValueChange={(value) => updateTeamField("useLlmJudgment", value)}
        />
      </div>

      {/* Selector Prompt (only for selector strategy) */}
      {team.strategy === "selector" && (
        <Textarea
          label={t("Selector Prompt")}
          maxRows={4}
          minRows={2}
          placeholder={t("Custom prompt for speaker selection...")}
          size="sm"
          value={team.selectorPrompt || ""}
          onValueChange={(value) => updateTeamField("selectorPrompt", value)}
        />
      )}

      {/* Aggregator Prompt (only for mixture strategy) */}
      {team.strategy === "mixture" && (
        <Textarea
          label={t("Aggregator Prompt")}
          maxRows={4}
          minRows={2}
          placeholder={t("Custom prompt for result aggregation...")}
          size="sm"
          value={team.aggregatorPrompt || ""}
          onValueChange={(value) => updateTeamField("aggregatorPrompt", value)}
        />
      )}

      {/* Members */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs text-default-600">{t("Members")}</label>
          {availableAgents.length > 0 && (
            <Dropdown>
              <DropdownTrigger>
                <Button isIconOnly aria-label={t("Add member")} size="sm" variant="flat">
                  <Icon className="w-4 h-4" icon="solar:add-circle-linear" />
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label={t("Available agents")}
                onAction={(key) => addMember(String(key))}
              >
                {availableAgents.map((agentId) => (
                  <DropdownItem
                    key={agentId}
                    startContent={
                      <Icon className="w-4 h-4 text-blue-500" icon="solar:user-hand-up-outline" />
                    }
                  >
                    {agentId}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          )}
        </div>
        {team.members.length === 0 ? (
          <p className="text-xs text-default-400">
            {t("No members. Use the + button or connect agents to this team.")}
          </p>
        ) : (
          <div className="space-y-1">
            {team.members.map((memberId) => (
              <div
                key={memberId}
                className="flex items-center justify-between p-2 rounded-lg bg-default-100 cursor-pointer hover:bg-default-200 transition-colors"
                role="button"
                tabIndex={0}
                title={t("Click to edit agent")}
                onClick={() => setSelectedNode(memberId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setSelectedNode(memberId);
                }}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-blue-500" icon="solar:user-hand-up-outline" />
                  <span className="text-xs font-medium">{memberId}</span>
                  <Icon className="w-3 h-3 text-default-400" icon="solar:pen-linear" />
                </div>
                <Button
                  isIconOnly
                  aria-label={t("Remove member")}
                  size="sm"
                  variant="light"
                  onPress={() => removeMember(memberId)}
                >
                  <Icon className="w-4 h-4 text-danger" icon="solar:close-circle-linear" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
